import type { CommandStatus, ConfirmationChoice, Measurement, Mode, StateReason, Weekday } from '../api/types';

export const MODE_LABEL: Record<Mode, string> = {
  confort: 'Confort',
  eco: 'Éco',
  hors_gel: 'Hors-gel',
  arret: 'Arrêt',
};

export const REASON_LABEL: Record<StateReason, string> = {
  suspended: 'Radiateur suspendu',
  measurement: 'Mesure de déperdition en cours',
  override: 'Forçage manuel',
  long_absence: 'Absence longue',
  routine: 'Routine',
  routine_held: 'Routine · mode conservé',
  routine_before_first_phase: 'Avant la première phase du jour',
  fallback: 'Aucune routine ce jour · repli Arrêt',
};

export const WAITING_LABEL: Record<'presence_required' | 'ended_on_departure', string> = {
  presence_required: 'attend le retour de présence',
  ended_on_departure: 'phase terminée au départ, suivante en attente',
};

export const COMMAND_LABEL: Record<CommandStatus, { label: string; tone: Tone }> = {
  unknown: { label: 'État inconnu', tone: 'neutral' },
  pending: { label: 'Commande en attente', tone: 'warn' },
  applying: { label: 'Application en cours', tone: 'warn' },
  confirmed: { label: 'Confirmé par le module', tone: 'ok' },
  failed: { label: 'Échec de commande', tone: 'bad' },
};

export type Tone = 'ok' | 'warn' | 'bad' | 'neutral' | 'info';

export const WEEKDAY_SHORT: Record<Weekday, string> = {
  mon: 'lun', tue: 'mar', wed: 'mer', thu: 'jeu', fri: 'ven', sat: 'sam', sun: 'dim',
};

export const CHOICE_LABEL: Record<ConfirmationChoice, string> = {
  confirm_exclusion: 'Exclure les radiateurs suspendus et continuer',
  cancel_measurement_keep_wake: 'Garder le réveil, annuler la mesure',
  cancel_wake_keep_measurement: 'Garder la mesure, annuler le réveil',
  confirm_exclusion_and_cancel_wake_keep_measurement: 'Exclure les radiateurs suspendus et annuler le réveil',
};

export const MEASUREMENT_STATUS_LABEL: Record<Measurement['status'], { label: string; tone: Tone }> = {
  scheduled: { label: 'Programmée', tone: 'info' },
  running: { label: 'En cours', tone: 'warn' },
  completed: { label: 'Terminée', tone: 'ok' },
  cancelled: { label: 'Annulée', tone: 'neutral' },
  failed: { label: 'Échouée', tone: 'bad' },
  annulee_capteur_absent: { label: 'Annulée · sonde absente', tone: 'neutral' },
};

export const STAGE_LABEL: Record<NonNullable<Measurement['stage']>, string> = {
  initial_heating: 'Mise en température',
  cooling: 'Refroidissement',
  reheating: 'Remontée',
  completed: 'Terminé',
};

export const OUTCOME_LABEL: Record<NonNullable<Measurement['outcome']>, string> = {
  all_targets_reached: 'Toutes les cibles atteintes',
  plateau_before_target: 'Plateau avant une cible',
  interrupted: 'Test interrompu',
};

export const FAILURE_LABEL: Record<NonNullable<Measurement['failure_reason']>, string> = {
  sensor_timeout: 'Sonde sans mesure depuis 15 minutes',
  sensor_disagreement: 'Écart de plus de 2 °C entre les sondes',
  radiator_fault: 'Panne d’un radiateur participant',
  service_restart: 'Redémarrage du service',
  max_duration_exceeded: 'Durée maximale de 10 h dépassée',
};

export const CANCELLATION_LABEL: Record<NonNullable<Measurement['cancellation_reason']>, string> = {
  sensor_suspended: 'Sonde suspendue',
  missed_start: 'Démarrage manqué',
  annulee_capteur_absent: 'Sonde indisponible au démarrage',
  cancelled_by_wake_conflict: 'Annulée pour garder un réveil',
  cancelled_by_user: 'Annulée manuellement',
  configuration_changed: 'Configuration modifiée',
};

/** Messages des codes d'erreur métier (x-error-codes). */
export const ERROR_MESSAGES: Record<string, string> = {
  UNAUTHORIZED: 'Accès refusé par le service.',
  VALIDATION_ERROR: 'Certaines valeurs sont invalides.',
  IDEMPOTENCY_KEY_REQUIRED: 'Requête refusée : clé d’idempotence manquante.',
  VERSION_REQUIRED: 'Version manquante : rechargez la page.',
  VERSION_CONFLICT: 'Cet élément a été modifié entre-temps. Les données ont été rechargées, recommencez.',
  IDEMPOTENCY_CONFLICT: 'Requête en double refusée.',
  NOT_FOUND: 'Élément introuvable (supprimé ?).',
  MEASUREMENT_IN_PROGRESS: 'Une mesure est en cours : seules l’annulation du test et la présence sont acceptées.',
  WAKE_ALREADY_TRIGGERED: 'Ce réveil est déjà déclenché : il ne peut plus être modifié ni annulé.',
  LONG_ABSENCE_ACTIVE: 'Une absence longue est en cours. Arrêtez-la d’abord pour programmer un réveil.',
  MEASUREMENT_ALREADY_SCHEDULED: 'Une mesure est déjà programmée.',
  MEASUREMENT_WAKE_CONFLICT: 'Conflit entre un réveil et la mesure programmée.',
  CONFIRMATION_EXPIRED: 'La confirmation a expiré (5 minutes). Recommencez.',
  CONFIRMATION_STALE: 'La situation a changé depuis la demande. Recommencez.',
  API_VERSION_UNSUPPORTED: 'Version de l’API non prise en charge : modifications désactivées.',
};
